import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { AnimatePresence, motion, MotionConfig } from 'motion/react';
import {
  LayoutDashboard,
  Users,
  Kanban,
  Building2,
  Waypoints,
  CalendarDays,
  CheckSquare,
  Megaphone,
  MessagesSquare,
  Workflow,
  ChartNoAxesCombined,
  Handshake,
  Settings2,
  Search,
  Bell,
  Plus,
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  PanelLeftClose,
  PanelLeftOpen,
  Command,
  X,
  Check,
  TriangleAlert,
  ArrowRight,
  Leaf,
  Compass,
  Database,
  Target,
  Sparkles,
  Filter,
  UsersRound,
  MousePointer2,
} from 'lucide-react';
import { CRM } from './lib/context';
import { NotificationInbox } from './components/NotificationInbox';
import { api, initials } from './lib/api';
import { canWriteEntity, canReadWebsiteTracking } from './lib/permissions';
import { workspaceLabel } from './lib/runtime';
import { canManageTeam } from './lib/team';
import { emptyData, type Page, type Entity } from './lib/types';
import { Avatar, Badge, Skeleton } from './components/ui';
import { RecordForm } from './components/RecordForm';
import { Modal } from './components/Modal';
import { Overview, ContactsPage, PipelinePage, PropertiesPage, MatchesPage } from './pages/Core';
import { ContactDrawer } from './components/ContactDrawer';
import { BrandLogo } from './components/BrandLogo';
const LoginPage = lazy(() =>
  import('./components/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const Acquisition = lazy(() =>
  import('./pages/acquisition').then((m) => ({ default: m.AcquisitionPage })),
);
const Team = lazy(() => import('./pages/TeamPage').then((m) => ({ default: m.TeamPage })));
const WebsiteTracking = lazy(() =>
  import('./pages/WebsiteTrackingPage').then((m) => ({ default: m.WebsiteTrackingPage })),
);
const BusinessRelationships = lazy(() =>
  import('./pages/Relationships').then((m) => ({
    default: ({ page }: { page: Page }) =>
      page === 'accounts' ? <m.AccountsPage /> : <m.LeadsPage />,
  })),
);
const Operations = lazy(() =>
  import('./pages/Operations').then((m) => ({
    default: ({ page }: { page: Page }) => {
      const mapping: any = {
        visits: m.VisitsPage,
        tasks: m.TasksPage,
        campaigns: m.CampaignsPage,
        communications: m.CommunicationsPage,
        automations: m.AutomationsPage,
        analytics: m.AnalyticsPage,
        suppliers: m.SuppliersPage,
        settings: m.SettingsPage,
      };
      const C = mapping[page];
      return C ? <C /> : null;
    },
  })),
);
const navigation = [
  {
    label: 'WORKSPACE',
    items: [
      { id: 'overview', label: 'Overview', icon: LayoutDashboard },
      { id: 'team', label: 'Team & work', icon: UsersRound },
      { id: 'contacts', label: 'Relationships', icon: Users },
      { id: 'leads', label: 'Enquiry desk', icon: Compass },
      { id: 'accounts', label: 'Company accounts', icon: Building2 },
      { id: 'pipeline', label: 'Deal pipeline', icon: Kanban },
    ],
  },
  {
    label: 'ACQUISITION',
    items: [
      { id: 'data-input', label: 'Data input', icon: Database },
      { id: 'prospect-discovery', label: 'Prospect discovery', icon: Compass },
      { id: 'prospect-pool', label: 'Prospect pool', icon: Users },
      { id: 'targeting-audiences', label: 'Targeting & audiences', icon: Target },
      { id: 'ad-strategy', label: 'Ad strategy', icon: Sparkles },
      { id: 'funnel-performance', label: 'Funnel performance', icon: Filter },
    ],
  },
  {
    label: 'INTELLIGENCE',
    items: [
      { id: 'properties', label: 'Property portfolio', icon: Building2 },
      { id: 'matches', label: 'Matching studio', icon: Waypoints },
      { id: 'analytics', label: 'Revenue insights', icon: ChartNoAxesCombined },
      { id: 'website-tracking', label: 'Website activity', icon: MousePointer2 },
    ],
  },
  {
    label: 'OPERATIONS',
    items: [
      { id: 'visits', label: 'Site visits', icon: CalendarDays },
      { id: 'tasks', label: 'Tasks & follow-ups', icon: CheckSquare },
      { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
      { id: 'communications', label: 'Communication', icon: MessagesSquare },
      { id: 'automations', label: 'Automations', icon: Workflow },
      { id: 'suppliers', label: 'Supply partners', icon: Handshake },
    ],
  },
];
const allNav = navigation.flatMap((g) => g.items);
export default function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 700);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const [data, setData] = useState(emptyData);
  const [page, setPage] = useState<Page>(
    () =>
      (allNav.some((x) => x.id === location.hash.slice(1)) || location.hash === '#settings'
        ? location.hash.slice(1)
        : 'overview') as Page,
  );
  const [loading, setLoading] = useState(true);
  const refreshGeneration = useRef(0);
  const [loadError, setLoadError] = useState('');
  const [authRequired, setAuthRequired] = useState(false);
  const [mfaReceipt, setMfaReceipt] = useState<{ recoveryCodes: string[] } | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [contact, setContact] = useState<Entity | null>(null);
  const [create, setCreate] = useState<{ type: string; defaults?: Record<string, any> } | null>(
    null,
  );
  const [search, setSearch] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; type: string } | null>(null);
  const [quick, setQuick] = useState(false);
  const notify = useCallback((text: string, type = 'success') => {
    setToast({ text, type });
  }, []);
  const refresh = useCallback(async () => {
    const generation = ++refreshGeneration.current;
    const result = await api('/bootstrap');
    if (result.authMode === 'local') await api('/auth/me');
    if (generation !== refreshGeneration.current) return;
    setData(result.data?.contacts ? result.data : result);
    setAuthRequired(false);
    setLoadError('');
  }, []);
  useEffect(() => {
    const expired = () => {
      refreshGeneration.current++;
      setAuthRequired(true);
      setData(emptyData);
      setContact(null);
      setCreate(null);
      setSearch(null);
    };
    const mfaChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      setMfaReceipt({
        recoveryCodes: Array.isArray(detail?.recoveryCodes) ? detail.recoveryCodes : [],
      });
      expired();
    };
    window.addEventListener('crm:session-expired', expired);
    window.addEventListener('crm:mfa-changed', mfaChanged);
    return () => {
      window.removeEventListener('crm:session-expired', expired);
      window.removeEventListener('crm:mfa-changed', mfaChanged);
    };
  }, []);
  useEffect(() => {
    (async () => {
      const auth = await api('/auth/config', 'GET', undefined, false);
      if (auth.demoAvailable) await api('/auth/me', 'GET', undefined, false);
      await refresh();
    })()
      .catch((e) => {
        if (e.status === 401 || e.code === 'PASSWORD_CHANGE_REQUIRED') setAuthRequired(true);
        else setLoadError(e.message);
      })
      .finally(() => setLoading(false));
  }, [refresh]);
  useEffect(() => {
    if (toast) {
      const id = setTimeout(() => setToast(null), 4500);
      return () => clearTimeout(id);
    }
  }, [toast]);
  const navigate = useCallback((next: Page) => {
    setPage(next);
    location.hash = next;
    setMobileNav(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearch('');
      }
      if (e.key === 'Escape') {
        setSearch(null);
        setQuick(false);
      }
    };
    const hash = () => {
      const p = location.hash.slice(1);
      if ([...allNav.map((x) => x.id), 'settings'].includes(p)) setPage(p as Page);
    };
    window.addEventListener('keydown', key);
    window.addEventListener('hashchange', hash);
    return () => {
      window.removeEventListener('keydown', key);
      window.removeEventListener('hashchange', hash);
    };
  }, []);
  const mutate = useCallback(
    async (path: string, method = 'POST', body?: unknown) => {
      try {
        const result = await api(path, method, body);
        try {
          await refresh();
        } catch {
          setLoadError(
            'The change was saved, but the workspace could not refresh. Retry loading to see the saved result.',
          );
        }
        return result;
      } catch (e) {
        notify((e as Error).message, 'error');
        throw e;
      }
    },
    [refresh, notify],
  );
  const openCreate = useCallback(
    (type: string, defaults?: Record<string, any>) => {
      if (!canWriteEntity(data.user, type, defaults?.id ? defaults : undefined)) {
        notify('Your role cannot create or edit this record type.', 'error');
        return;
      }
      setCreate({ type, defaults });
      setQuick(false);
    },
    [data.user, notify],
  );
  const liveContact = contact ? data.contacts.find((c) => c.id === contact.id) || contact : null;
  const current = allNav.find((n) => n.id === page);
  const dueTasks = data.tasks.filter((t) => !t.completed);
  const matches = (
    search === null
      ? []
      : data.contacts.filter((c) =>
          [c.name, c.company, c.email, c.phone, c.notes, ...(c.localities || []), ...(c.tags || [])]
            .join(' ')
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
  ).slice(0, 6);
  const propertyResults = search
    ? data.properties
        .filter((p) =>
          [p.name, p.locality, p.developer].join(' ').toLowerCase().includes(search.toLowerCase()),
        )
        .slice(0, 4)
    : [];
  const opportunityResults = search
    ? data.opportunities
        .filter((o) =>
          [o.title, o.nextAction, o.stage].join(' ').toLowerCase().includes(search.toLowerCase()),
        )
        .slice(0, 3)
    : [];
  const taskResults = search
    ? data.tasks
        .filter((t) => [t.title, t.owner].join(' ').toLowerCase().includes(search.toLowerCase()))
        .slice(0, 3)
    : [];
  if (mfaReceipt)
    return (
      <main style={{ maxWidth: 640, margin: '8vh auto', padding: 24 }}>
        <h1>Sign-in protection updated</h1>
        {mfaReceipt.recoveryCodes.length > 0 && (
          <>
            <p>
              Save these one-use recovery codes in protected storage. They are shown only now. Wait
              for the next authenticator code before signing in again.
            </p>
            <pre style={{ whiteSpace: 'pre-wrap', userSelect: 'all' }}>
              {mfaReceipt.recoveryCodes.join('\n')}
            </pre>
          </>
        )}
        <p>All previous sessions have been signed out.</p>
        <button
          className="btn btn-primary"
          onClick={() => {
            setMfaReceipt(null);
            window.location.reload();
          }}
        >
          {mfaReceipt.recoveryCodes.length
            ? 'I saved my codes — return to sign-in'
            : 'Return to sign-in'}
        </button>
      </main>
    );
  if (authRequired)
    return (
      <MotionConfig reducedMotion="user">
        <Suspense fallback={<div className="login-loading">Opening your workspace…</div>}>
          <LoginPage onAuthenticated={refresh} />
        </Suspense>
      </MotionConfig>
    );
  return (
    <CRM.Provider
      value={{ data, refresh, notify, navigate, openContact: setContact, openCreate, mutate }}
    >
      <MotionConfig reducedMotion="user">
        <div className={`app-shell ${collapsed ? 'nav-collapsed' : ''}`}>
          {mobileNav && <div className="mobile-nav-backdrop" onClick={() => setMobileNav(false)} />}
          <aside
            id="main-sidebar"
            inert={isMobile && !mobileNav}
            aria-hidden={(isMobile && !mobileNav) || undefined}
            className={`sidebar ${mobileNav ? 'mobile-open' : ''}`}
          >
            <a
              href="#overview"
              className="brand"
              onClick={() => navigate('overview')}
              aria-label="The Indian Agency home"
            >
              <BrandLogo tone="light" compact={collapsed} />
            </a>
            <div className="workspace-switch">
              <span className="workspace-icon">
                <Building2 size={17} />
              </span>
              <div>
                <strong>
                  {data.runtime?.mode === 'real'
                    ? 'Real workspace'
                    : data.runtime?.mode === 'demo'
                      ? 'Demo workspace'
                      : 'Workspace'}
                </strong>
                <small>
                  <i />{' '}
                  {data.runtime?.mode === 'real'
                    ? 'Saved CRM data'
                    : data.runtime?.mode === 'demo'
                      ? 'Demonstration data'
                      : 'Checking workspace mode'}
                </small>
              </div>
              <ChevronDown size={14} />
            </div>
            <nav aria-label="Main navigation">
              {navigation.map((group) => (
                <div className="nav-group" key={group.label}>
                  <div className="nav-label">{group.label}</div>
                  {group.items
                    .filter((item) => item.id !== 'team' || canManageTeam(data.user.role))
                    .filter(
                      (item) =>
                        item.id !== 'website-tracking' || canReadWebsiteTracking(data.user.role),
                    )
                    .map((item) => (
                      <button
                        title={item.label}
                        aria-label={item.label}
                        aria-current={page === item.id ? 'page' : undefined}
                        key={item.id}
                        onClick={() => navigate(item.id as Page)}
                        className={`nav-item ${page === item.id ? 'selected' : ''}`}
                      >
                        {page === item.id && (
                          <motion.div
                            layoutId="navigation-pill"
                            className="nav-pill"
                            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                          />
                        )}
                        <item.icon size={18} />
                        <span>{item.label}</span>
                        {item.id === 'tasks' && dueTasks.length > 0 && <b>{dueTasks.length}</b>}
                        {item.id === 'matches' && <i className="nav-spark" />}
                      </button>
                    ))}
                </div>
              ))}
            </nav>
            <div className="sidebar-bottom">
              <div className="workspace-card">
                <div>
                  <strong>Built for meaningful moves.</strong>
                  <p>Intelligence meets relationships.</p>
                </div>
                <div className="workspace-card-glow" />
              </div>
              <button
                className={`nav-item ${page === 'settings' ? 'selected' : ''}`}
                aria-label="Workspace settings"
                title="Workspace settings"
                aria-current={page === 'settings' ? 'page' : undefined}
                onClick={() => navigate('settings')}
              >
                <Settings2 size={18} />
                <span>Workspace settings</span>
              </button>
              <button
                className="profile-button"
                aria-label={`Profile settings for ${data.user?.name || 'your account'}`}
                title="Profile settings"
                onClick={() => navigate('settings')}
              >
                <Avatar name={data.user?.name || 'Staff account'} size={37} />
                <div>
                  <strong>{data.user?.name || 'Staff account'}</strong>
                  <small>{data.user?.role || 'Access unavailable'}</small>
                </div>
                <ChevronDown size={15} />
              </button>
            </div>
          </aside>
          <div className="main-shell" inert={isMobile && mobileNav}>
            <header className="topbar">
              <div className="breadcrumb">
                <button
                  className="icon-btn desktop-collapse"
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  onClick={() => setCollapsed(!collapsed)}
                >
                  {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                </button>
                <button
                  className="icon-btn mobile-menu"
                  aria-label="Open menu"
                  aria-controls="main-sidebar"
                  aria-expanded={mobileNav}
                  onClick={() => setMobileNav(!mobileNav)}
                >
                  <PanelLeftOpen size={20} />
                </button>
                <span>Workspace</span>
                <ChevronRight size={13} />
                <strong>{current?.label || 'Settings'}</strong>
              </div>
              <div className="topbar-actions">
                <button
                  className="global-search"
                  aria-label="Search workspace"
                  onClick={() => setSearch('')}
                >
                  <Search size={16} />
                  <span>Search anything...</span>
                  <kbd>⌘ K</kbd>
                </button>
                <span className="topbar-divider" />
                <NotificationInbox />
                <div className="quick-anchor">
                  <button
                    className="btn btn-primary btn-quick"
                    aria-label="Quick create"
                    onClick={() => setQuick(!quick)}
                  >
                    <Plus size={17} />
                    <span>Quick create</span>
                  </button>
                  {quick && (
                    <div className="quick-popover">
                      {[
                        ['contacts', 'Relationship', Users],
                        ['opportunities', 'Opportunity', Kanban],
                        ['properties', 'Property', Building2],
                        ['visits', 'Site visit', CalendarDays],
                        ['tasks', 'Task', CheckSquare],
                      ]
                        .filter(([id]) => canWriteEntity(data.user, String(id)))
                        .map(([id, label, Icon]: any) => (
                          <button key={id} onClick={() => openCreate(id)}>
                            <Icon size={17} />
                            {label}
                            <Plus size={14} />
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </header>
            <main className="main-content" id="main-content">
              {loading ? (
                <Skeleton />
              ) : loadError ? (
                <div className="connection-error">
                  <TriangleAlert size={32} />
                  <h1>Let's reconnect your workspace</h1>
                  <p>{loadError}</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setLoading(true);
                      refresh()
                        .catch((e) => setLoadError(e.message))
                        .finally(() => setLoading(false));
                    }}
                  >
                    Try again
                  </button>
                  <small>Start the web and API together with npm run dev.</small>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={page}
                    initial={{ opacity: 0, y: 9 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.22 }}
                  >
                    {page === 'overview' ? (
                      <Overview />
                    ) : page === 'contacts' ? (
                      <ContactsPage />
                    ) : page === 'pipeline' ? (
                      <PipelinePage />
                    ) : page === 'properties' ? (
                      <PropertiesPage />
                    ) : page === 'matches' ? (
                      <MatchesPage />
                    ) : page === 'website-tracking' ? (
                      <Suspense fallback={<Skeleton />}>
                        <WebsiteTracking />
                      </Suspense>
                    ) : page === 'team' ? (
                      <Suspense fallback={<Skeleton />}>
                        <Team />
                      </Suspense>
                    ) : page === 'accounts' || page === 'leads' ? (
                      <Suspense fallback={<Skeleton />}>
                        <BusinessRelationships page={page} />
                      </Suspense>
                    ) : [
                        'data-input',
                        'prospect-discovery',
                        'prospect-pool',
                        'targeting-audiences',
                        'ad-strategy',
                        'funnel-performance',
                      ].includes(page) ? (
                      <Suspense fallback={<Skeleton />}>
                        <Acquisition page={page} />
                      </Suspense>
                    ) : (
                      <Suspense fallback={<Skeleton />}>
                        <Operations page={page} />
                      </Suspense>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
              <footer className="app-footer">
                <span>
                  <span className="footer-mark">✧</span> THE INDIAN AGENCY
                </span>
                <span>
                  {workspaceLabel(data.runtime)}
                  <i />
                  Asia / Kolkata
                </span>
              </footer>
            </main>
          </div>
          <AnimatePresence>
            {liveContact && (
              <ContactDrawer
                key={liveContact.id}
                contact={liveContact}
                onClose={() => setContact(null)}
              />
            )}
          </AnimatePresence>
          <AnimatePresence>
            {create && (
              <RecordForm
                key={create.type}
                type={create.type}
                defaults={create.defaults}
                onClose={() => setCreate(null)}
              />
            )}
          </AnimatePresence>
          {search !== null && (
            <Modal
              title="Find your next move"
              description="Search relationships, properties, or jump to a workspace."
              onClose={() => setSearch(null)}
            >
              <div className="command-input">
                <Search size={20} />
                <input
                  aria-label="Search workspace"
                  autoFocus
                  placeholder="Search names, properties, localities..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <kbd>ESC</kbd>
              </div>
              <div className="command-results">
                <div className="eyebrow">RELATIONSHIPS</div>
                {matches.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setContact(c);
                      setSearch(null);
                    }}
                  >
                    <Avatar name={c.name} size={34} />
                    <span>
                      <strong>{c.name}</strong>
                      <small>
                        {c.company} · {c.localities?.join(', ')}
                      </small>
                    </span>
                    <ArrowUpRight size={15} />
                  </button>
                ))}
                {propertyResults.length > 0 && <div className="eyebrow">PROPERTIES</div>}
                {propertyResults.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      navigate('properties');
                      setSearch(null);
                    }}
                  >
                    <Building2 size={22} />
                    <span>
                      <strong>{p.name}</strong>
                      <small>{p.locality}</small>
                    </span>
                    <ArrowUpRight size={15} />
                  </button>
                ))}
                {opportunityResults.length > 0 && <div className="eyebrow">OPPORTUNITIES</div>}
                {opportunityResults.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => {
                      navigate('pipeline');
                      setSearch(null);
                      const c = data.contacts.find((c) => c.id === o.contactId);
                      if (c) setContact(c);
                    }}
                  >
                    <Kanban size={18} />
                    <span>
                      <strong>{o.title}</strong>
                      <small>
                        {o.stage} · {o.owner}
                      </small>
                    </span>
                    <ArrowUpRight size={15} />
                  </button>
                ))}
                {taskResults.length > 0 && <div className="eyebrow">TASKS</div>}
                {taskResults.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => {
                      navigate('tasks');
                      setSearch(null);
                    }}
                  >
                    <CheckSquare size={18} />
                    <span>
                      <strong>{t.title}</strong>
                      <small>
                        {t.completed ? 'Completed' : `${t.priority} priority`} · {t.owner}
                      </small>
                    </span>
                    <ArrowUpRight size={15} />
                  </button>
                ))}
                <div className="eyebrow">JUMP TO</div>
                {allNav
                  .filter(
                    (n) => n.id !== 'website-tracking' || canReadWebsiteTracking(data.user.role),
                  )
                  .filter((n) => !search || n.label.toLowerCase().includes(search.toLowerCase()))
                  .slice(0, search ? 8 : 3)
                  .map((n) => (
                    <button
                      key={n.id}
                      onClick={() => {
                        navigate(n.id as Page);
                        setSearch(null);
                      }}
                    >
                      <n.icon size={18} />
                      <span>{n.label}</span>
                      <ArrowRight size={15} />
                    </button>
                  ))}
                {search &&
                  matches.length === 0 &&
                  propertyResults.length === 0 &&
                  opportunityResults.length === 0 &&
                  taskResults.length === 0 && (
                    <p className="muted">No matching records. Try a different name or locality.</p>
                  )}
              </div>
            </Modal>
          )}
          <AnimatePresence>
            {toast && (
              <motion.div
                role="status"
                className={`toast ${toast.type === 'error' ? 'toast-error' : ''}`}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10 }}
              >
                {toast.type === 'error' ? (
                  <TriangleAlert size={18} />
                ) : (
                  <span className="toast-check">
                    <Check size={13} />
                  </span>
                )}
                <span>{toast.text}</span>
                <button onClick={() => setToast(null)} aria-label="Dismiss notification">
                  <X size={15} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </MotionConfig>
    </CRM.Provider>
  );
}
